import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  ContactImportCommitResult,
  ContactImportDuplicateAction,
  ContactImportPreviewResult,
} from '@neuroedge/shared';
import { Button, Card, useToast } from '../../components/ui';
import {
  downloadContactImportTemplate,
  usePreviewContactImport,
  useCommitContactImport,
} from '../../api/contacts';

const ALLOWED_EXTENSIONS = ['.csv', '.xlsx', '.vcf'];
const MAX_FILE_BYTES = 5 * 1024 * 1024;

function isAllowedFile(file: File): boolean {
  const lower = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Bulk-import page for contacts: drag-drop or pick a `.csv`/`.xlsx`/`.vcf`
 * file, download the template, validate (preview) before writing anything,
 * then commit with a skip-vs-override choice for any rows that collide with
 * an existing contact by phone. Preview and commit each re-upload the same
 * `File` object - nothing is held open server-side between the two calls.
 * Mirrors `products-services/ImportModal.tsx`'s UX, as a standalone page
 * (reached from a button on `ContactsPage`) rather than a modal.
 */
export function ContactsImportPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<ContactImportPreviewResult | null>(null);
  const [duplicateAction, setDuplicateAction] = useState<ContactImportDuplicateAction>('SKIP');
  const [result, setResult] = useState<ContactImportCommitResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const previewImport = usePreviewContactImport();
  const commitImport = useCommitContactImport();

  function pickFile(candidate: File | null) {
    setPreview(null);
    setResult(null);
    setFileError(null);
    if (!candidate) {
      setFile(null);
      return;
    }
    if (!isAllowedFile(candidate)) {
      setFileError('Only .csv, .xlsx, or .vcf files are allowed.');
      setFile(null);
      return;
    }
    if (candidate.size > MAX_FILE_BYTES) {
      setFileError('File must be 5MB or smaller.');
      setFile(null);
      return;
    }
    setFile(candidate);
  }

  async function runPreview() {
    if (!file) return;
    try {
      const p = await previewImport.mutateAsync(file);
      setPreview(p);
      setDuplicateAction('SKIP');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not validate the file.');
    }
  }

  async function runCommit() {
    if (!file) return;
    try {
      const r = await commitImport.mutateAsync({ file, duplicateAction });
      setResult(r);
      toast.success('Import finished.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not import the file.');
    }
  }

  function startOver() {
    setFile(null);
    setPreview(null);
    setResult(null);
    setFileError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 760 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Import contacts</h1>

      <Card>
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Upload a .csv, .xlsx, or .vcf (vCard) file with your contacts.{' '}
          <button
            type="button"
            onClick={() => void downloadContactImportTemplate()}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--color-primary)',
              textDecoration: 'underline',
              cursor: 'pointer',
              font: 'inherit',
            }}
          >
            Download the template
          </button>{' '}
          to see the expected columns.
        </p>

        <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-3)' }}>
          {!result ? (
            <div
              className={dragActive ? 'ne-dropzone ne-dropzone--active' : 'ne-dropzone'}
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                pickFile(e.dataTransfer.files[0] ?? null);
              }}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.vcf"
                style={{ display: 'none' }}
                onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
              />
              {file ? (
                <span>
                  {file.name} · {(file.size / 1024).toFixed(0)} KB
                </span>
              ) : (
                <span>Drag and drop a file here, or click to browse.</span>
              )}
            </div>
          ) : null}

          {fileError ? (
            <p style={{ margin: 0, color: 'var(--color-danger)' }}>{fileError}</p>
          ) : null}

          {!preview && !result ? (
            <Button onClick={() => void runPreview()} disabled={!file || previewImport.isPending}>
              {previewImport.isPending ? 'Checking…' : 'Validate'}
            </Button>
          ) : null}

          {preview && !result ? (
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <span className="ne-badge">{preview.totalRows} rows</span>
                <span className="ne-badge">{preview.validCount} ready to import</span>
                {preview.duplicates.length > 0 ? (
                  <span className="ne-badge">{preview.duplicates.length} duplicates</span>
                ) : null}
                {preview.invalidRows.length > 0 ? (
                  <span className="ne-badge">
                    {preview.invalidRows.length} invalid (will be skipped)
                  </span>
                ) : null}
              </div>

              {preview.invalidRows.length > 0 ? (
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  <strong style={{ fontSize: 'var(--font-size-sm)' }}>Invalid rows</strong>
                  <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                    {preview.invalidRows.map((r) => (
                      <li key={r.row} style={{ fontSize: 'var(--font-size-sm)' }}>
                        Row {r.row}: {r.errors.join('; ')}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {preview.duplicates.length > 0 ? (
                <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
                  <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                    <strong style={{ fontSize: 'var(--font-size-sm)' }}>Duplicate rows</strong>
                    <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                      {preview.duplicates.map((d) => (
                        <li key={d.row} style={{ fontSize: 'var(--font-size-sm)' }}>
                          Row {d.row}: "{d.displayName}" matches existing "{d.existingDisplayName}"
                          {d.phone ? ` (${d.phone})` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                    <legend style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                      For duplicate rows:
                    </legend>
                    <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <input
                        type="radio"
                        name="duplicateAction"
                        checked={duplicateAction === 'SKIP'}
                        onChange={() => setDuplicateAction('SKIP')}
                      />
                      Skip - keep the existing contact unchanged
                    </label>
                    <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                      <input
                        type="radio"
                        name="duplicateAction"
                        checked={duplicateAction === 'OVERRIDE'}
                        onChange={() => setDuplicateAction('OVERRIDE')}
                      />
                      Override - replace the existing contact with the file's values
                    </label>
                  </fieldset>
                </div>
              ) : null}

              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button
                  onClick={() => void runCommit()}
                  disabled={
                    preview.validCount + preview.duplicates.length === 0 || commitImport.isPending
                  }
                >
                  {commitImport.isPending ? 'Importing…' : 'Import'}
                </Button>
                <Button variant="secondary" onClick={startOver} disabled={commitImport.isPending}>
                  Choose a different file
                </Button>
              </div>
            </div>
          ) : null}

          {result ? (
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <span className="ne-badge">{result.created} created</span>
                <span className="ne-badge">{result.updated} updated</span>
                <span className="ne-badge">{result.skipped} skipped</span>
              </div>
              {result.invalidRows.length > 0 ? (
                <div style={{ maxHeight: 160, overflowY: 'auto' }}>
                  <strong style={{ fontSize: 'var(--font-size-sm)' }}>
                    Skipped - invalid rows
                  </strong>
                  <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 'var(--space-4)' }}>
                    {result.invalidRows.map((r) => (
                      <li key={r.row} style={{ fontSize: 'var(--font-size-sm)' }}>
                        Row {r.row}: {r.errors.join('; ')}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <Button onClick={() => navigate('/contacts')}>Done</Button>
                <Button variant="secondary" onClick={startOver}>
                  Import another file
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/contacts">← All contacts</Link>
      </p>
    </div>
  );
}
